import { Check, Trash2, UserPlus, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Avatar } from '../../components/Avatar.tsx';
import { ApiError, api } from '../../lib/api.ts';
import { useAuth } from '../../lib/auth.tsx';

interface AdminUser {
    id: string;
    email: string;
    display_name: string;
    avatarUrl: string | null;
    role: 'user' | 'coach' | 'admin';
    is_active: boolean;
    approvalStatus: 'pending' | 'approved' | 'rejected';
    approvalRequestedAt: string | null;
    email_verified: boolean;
    must_change_password: boolean;
    has_password: boolean;
    identity_count: number;
    session_count: number;
    shot_count: number;
    club_count: number;
    last_login_at: string | null;
    created_at: string;
}

const ROLES = [
    { value: 'user', label: 'Joueur' },
    { value: 'coach', label: 'Coach' },
    { value: 'admin', label: 'Administrateur' },
] as const;

export function AdminUsers() {
    const { user: me } = useAuth();
    const [users, setUsers] = useState<AdminUser[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [busyId, setBusyId] = useState<string | null>(null);
    const [inviting, setInviting] = useState(false);
    const [invite, setInvite] = useState({ email: '', displayName: '', role: 'user' });
    /** Suppression : on demande confirmation dans la carte elle-même, en
     *  affichant ce qui va disparaître. Un `confirm()` du navigateur ne peut
     *  pas énumérer les séances et les coups perdus. */
    const [confirmDelete, setConfirmDelete] = useState<AdminUser | null>(null);

    const load = useCallback(async () => {
        try {
            const { users: u } = await api<{ users: AdminUser[] }>('/admin/users');
            setUsers(u);
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Chargement impossible');
        }
    }, []);

    useEffect(() => { void load(); }, [load]);

    const call = async (fn: () => Promise<unknown>, id: string): Promise<void> => {
        setBusyId(id);
        setError(null);
        try {
            await fn();
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Opération impossible');
        } finally {
            setBusyId(null);
            await load();
        }
    };

    const patch = (id: string, body: Record<string, unknown>) =>
        call(() => api(`/admin/users/${id}`, { method: 'PATCH', body }), id);

    const decide = (id: string, decision: 'approved' | 'rejected') =>
        call(() => api(`/admin/users/${id}/approval`, { method: 'POST', body: { decision } }), id);

    const remove = (id: string) =>
        call(async () => {
            await api(`/admin/users/${id}`, { method: 'DELETE' });
            setConfirmDelete(null);
        }, id);

    const submitInvite = async (e: React.FormEvent): Promise<void> => {
        e.preventDefault();
        setError(null);
        try {
            await api('/admin/users', { method: 'POST', body: invite });
            setInvite({ email: '', displayName: '', role: 'user' });
            setInviting(false);
            await load();
        } catch (err) {
            setError(err instanceof ApiError ? err.message : 'Invitation impossible');
        }
    };

    const pending = users.filter((u) => u.approvalStatus === 'pending');
    const others = users.filter((u) => u.approvalStatus !== 'pending');

    return (
        <div>
            <div className="mb-4 flex items-center justify-between gap-3">
                <p className="text-sm opacity-60">{users.length} compte(s)</p>
                <button type="button" className="btn btn-primary btn-sm gap-2"
                    onClick={() => setInviting((v) => !v)}>
                    <UserPlus size={16} /> Inviter
                </button>
            </div>

            {inviting && (
                <form onSubmit={(e) => void submitInvite(e)}
                    className="mb-4 space-y-3 rounded-box bg-base-200 p-4">
                    <label className="block w-full">
                        <span className="mb-1 block text-sm">Adresse e-mail *</span>
                        <input type="email" required className="input input-bordered w-full"
                            value={invite.email} placeholder="joueur@exemple.fr"
                            onChange={(e) => setInvite((i) => ({ ...i, email: e.target.value }))} />
                        <span className="mt-1 block text-xs opacity-60">
                            Doit correspondre exactement au compte Google de la personne.
                            Le compte est créé approuvé, sans mot de passe.
                        </span>
                    </label>
                    <label className="block w-full">
                        <span className="mb-1 block text-sm">Nom affiché *</span>
                        <input required maxLength={120} className="input input-bordered w-full"
                            value={invite.displayName}
                            onChange={(e) => setInvite((i) => ({ ...i, displayName: e.target.value }))} />
                    </label>
                    <label className="block w-full max-w-52">
                        <span className="mb-1 block text-sm">Rôle</span>
                        <select className="select select-bordered w-full" value={invite.role}
                            onChange={(e) => setInvite((i) => ({ ...i, role: e.target.value }))}>
                            {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                        </select>
                    </label>
                    <div className="flex gap-2">
                        <button type="button" className="btn btn-ghost flex-1"
                            onClick={() => setInviting(false)}>Annuler</button>
                        <button type="submit" className="btn btn-primary flex-1">Créer le compte</button>
                    </div>
                </form>
            )}

            {error && (
                <div role="alert" className="alert alert-error mb-4 text-sm">
                    <span>{error}</span>
                    <button type="button" className="btn btn-ghost btn-xs" onClick={() => setError(null)}>OK</button>
                </div>
            )}

            {/* ── File d'attente ───────────────────────────────────────────── */}
            {pending.length > 0 && (
                <section className="mb-6">
                    <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider opacity-70">
                        Demandes en attente
                        <span className="badge badge-warning badge-sm">{pending.length}</span>
                    </h2>
                    <div className="space-y-2">
                        {pending.map((u) => (
                            <div key={u.id} className="rounded-box border border-warning/40 bg-warning/10 p-4">
                                <div className="flex items-center gap-3">
                                    <Avatar name={u.display_name} email={u.email}
                                        avatarUrl={u.avatarUrl} size={44} />
                                    <div className="min-w-0 flex-1">
                                        <div className="truncate font-semibold">{u.display_name}</div>
                                        <div className="truncate text-sm opacity-70">{u.email}</div>
                                        {u.approvalRequestedAt && (
                                            <div className="text-xs opacity-50">
                                                demandé le {new Date(u.approvalRequestedAt).toLocaleString('fr-FR', {
                                                    dateStyle: 'short', timeStyle: 'short',
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="mt-3 flex gap-2">
                                    <button type="button" disabled={busyId === u.id}
                                        onClick={() => void decide(u.id, 'approved')}
                                        className="btn btn-primary flex-1 gap-2">
                                        <Check size={16} /> Accepter
                                    </button>
                                    <button type="button" disabled={busyId === u.id}
                                        onClick={() => void decide(u.id, 'rejected')}
                                        className="btn btn-outline btn-error flex-1 gap-2">
                                        <X size={16} /> Refuser
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* ── Comptes ──────────────────────────────────────────────────── */}
            <div className="space-y-3">
                {others.map((u) => (
                    <div key={u.id} className="rounded-box bg-base-200 p-4">
                        <div className="flex items-start gap-3">
                            <Avatar name={u.display_name} email={u.email}
                                avatarUrl={u.avatarUrl} size={44} />
                            <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className="truncate font-semibold">{u.display_name}</span>
                                    {u.id === me?.id && <span className="badge badge-primary badge-sm">vous</span>}
                                    {!u.is_active && <span className="badge badge-error badge-sm">désactivé</span>}
                                    {u.approvalStatus === 'rejected' && (
                                        <span className="badge badge-error badge-sm">refusé</span>
                                    )}
                                    {u.must_change_password && (
                                        <span className="badge badge-warning badge-sm">mot de passe initial</span>
                                    )}
                                </div>
                                <div className="truncate text-sm opacity-60">{u.email}</div>
                                <div className="mt-1 text-xs opacity-50">
                                    {u.session_count} séance(s) · {u.club_count} club(s)
                                    {' · '}{u.has_password ? 'mot de passe' : 'sans mot de passe'}
                                    {u.identity_count > 0 && ' · Google'}
                                    {' · '}
                                    {u.last_login_at
                                        ? `vu le ${new Date(u.last_login_at).toLocaleDateString('fr-FR')}`
                                        : 'jamais connecté'}
                                </div>
                            </div>

                            <input
                                type="checkbox"
                                className="toggle toggle-primary shrink-0"
                                aria-label={`Activer ${u.display_name}`}
                                checked={u.is_active}
                                disabled={busyId === u.id || u.id === me?.id}
                                onChange={(e) => void patch(u.id, { isActive: e.target.checked })}
                            />
                        </div>

                        <div className="mt-3 flex items-center gap-2">
                            <select
                                className="select select-bordered select-sm max-w-48 flex-1"
                                value={u.role}
                                disabled={busyId === u.id || u.id === me?.id}
                                onChange={(e) => void patch(u.id, { role: e.target.value })}
                                aria-label={`Rôle de ${u.display_name}`}
                            >
                                {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                            </select>
                            {u.id !== me?.id && (
                                <button type="button" className="btn btn-ghost btn-sm text-error"
                                    aria-label={`Supprimer ${u.display_name}`}
                                    onClick={() => setConfirmDelete(u)}>
                                    <Trash2 size={16} />
                                </button>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {/* ── Confirmation de suppression ──────────────────────────────── */}
            {confirmDelete && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
                    <div className="w-full max-w-sm rounded-box bg-base-200 p-5">
                        <h2 className="text-lg font-bold">Supprimer ce compte ?</h2>
                        <div className="mt-3 flex items-center gap-3">
                            <Avatar name={confirmDelete.display_name} email={confirmDelete.email}
                                avatarUrl={confirmDelete.avatarUrl} size={40} />
                            <div className="min-w-0">
                                <div className="truncate font-semibold">{confirmDelete.display_name}</div>
                                <div className="truncate text-sm opacity-60">{confirmDelete.email}</div>
                            </div>
                        </div>

                        {/* Ce qui part avec, énuméré : une suppression qu'on
                            accepte sans en connaître la portée n'est pas un
                            consentement. */}
                        <p className="mt-4 text-sm opacity-80">Seront définitivement effacés :</p>
                        <ul className="mt-1 list-inside list-disc text-sm opacity-80">
                            <li>{confirmDelete.session_count} séance(s) et {confirmDelete.shot_count} coup(s)</li>
                            <li>{confirmDelete.club_count} club(s) et ses sacs</li>
                            <li>ses sessions, préférences et son lien Google</li>
                        </ul>
                        <p className="mt-3 text-xs opacity-60">
                            Le journal d'audit conserve la trace de ses actions. Cette opération est
                            irréversible — pour un retrait temporaire, désactivez plutôt le compte.
                        </p>

                        <div className="mt-4 space-y-2">
                            <button type="button" disabled={busyId === confirmDelete.id}
                                onClick={() => void remove(confirmDelete.id)}
                                className="btn btn-error btn-block gap-2">
                                <Trash2 size={18} /> Supprimer définitivement
                            </button>
                            <button type="button" className="btn btn-ghost btn-block"
                                onClick={() => setConfirmDelete(null)}>Annuler</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
